import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  Tooltip,
  IconButton,
  LinearProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  Collapse,
  useTheme,
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  PauseCircleOutline as SkipIcon,
  HelpOutline as NeverIcon,
  ErrorOutline as StalledIcon,
  KeyboardArrowDown as ArrowDownIcon,
  KeyboardArrowUp as ArrowUpIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { jobsService, type JobStatusRow } from '../services';
import { getApiErrorMessage } from '../lib/apiError';
import { JobHistoryPanel } from './JobHistoryPanel';

type StatusKey = 'success' | 'failed' | 'skipped' | 'running' | 'stalled' | 'never';

/**
 * Derive the displayed status from a JobStatusRow. Distinguishes between:
 *   - running   : in-flight (status='running' from server, finishedAt null)
 *   - stalled   : in-flight but startedAt > 2× interval ago — orphaned by a
 *                 process crash. Distinct from 'failed' since we never got
 *                 the error message.
 */
function statusOf(row: JobStatusRow): StatusKey {
  if (!row.lastRun) return 'never';
  if (row.lastRun.status === 'running') {
    const startedMs = new Date(row.lastRun.startedAt).getTime();
    const elapsed = Date.now() - startedMs;
    if (elapsed > 2 * row.intervalMs) return 'stalled';
    return 'running';
  }
  return row.lastRun.status as StatusKey;
}

function formatDuration(ms: number | null): string {
  if (ms == null) return '—';
  if (ms < 1000) return `${ms} ms`;
  if (ms < 60_000) return `${(ms / 1000).toFixed(1)} s`;
  const m = Math.floor(ms / 60_000);
  const s = Math.round((ms % 60_000) / 1000);
  return `${m}m ${s}s`;
}

const STATUS_DISPLAY: Record<
  StatusKey,
  { label: string; color: 'success' | 'error' | 'warning' | 'info' | 'default'; icon: React.ReactElement }
> = {
  success: { label: 'Success', color: 'success', icon: <CheckCircleIcon fontSize="small" /> },
  failed: { label: 'Failed', color: 'error', icon: <CancelIcon fontSize="small" /> },
  skipped: { label: 'Skipped', color: 'warning', icon: <SkipIcon fontSize="small" /> },
  running: { label: 'Running', color: 'info', icon: <CircularProgress size={14} thickness={5} /> },
  stalled: { label: 'Stalled', color: 'error', icon: <StalledIcon fontSize="small" /> },
  never: { label: 'Never run', color: 'default', icon: <NeverIcon fontSize="small" /> },
};

function StatusChip({ status }: { status: StatusKey }) {
  const m = STATUS_DISPLAY[status];
  const isAlert = status === 'failed' || status === 'stalled';
  return (
    <Chip
      size="small"
      color={m.color}
      icon={m.icon}
      label={m.label}
      role={isAlert ? 'alert' : undefined}
    />
  );
}

export const BackgroundJobsCard: React.FC = () => {
  const theme = useTheme();
  const [rows, setRows] = useState<JobStatusRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [expanded, setExpanded] = useState<Set<string>>(new Set());

  const toggleExpanded = (name: string) => {
    setExpanded((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  const load = useCallback(async () => {
    try {
      setError(null);
      const data = await jobsService.getStatus();
      setRows(data);
    } catch (err: any) {
      setError(getApiErrorMessage(err, 'Failed to load job status'));
    } finally {
      setLoading(false);
    }
  }, []);

  // Auto-refresh every 30s, but pause when the tab is hidden so a background
  // tab doesn't keep pinging /api/admin/jobs forever.
  useEffect(() => {
    load();
    let timer: ReturnType<typeof setInterval> | null = null;

    const start = () => {
      if (timer) return;
      timer = setInterval(load, 30_000);
    };
    const stop = () => {
      if (!timer) return;
      clearInterval(timer);
      timer = null;
    };
    const onVis = () => {
      if (document.visibilityState === 'visible') {
        load(); // refresh on return so the user doesn't see stale data
        start();
      } else {
        stop();
      }
    };

    if (document.visibilityState === 'visible') start();
    document.addEventListener('visibilitychange', onVis);

    return () => {
      stop();
      document.removeEventListener('visibilitychange', onVis);
    };
  }, [load]);

  return (
    <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 1 }}>
        <ScheduleIcon sx={{ color: 'primary.main' }} />
        <Typography variant="h6" fontWeight={600} sx={{ flex: 1 }}>
          Background Jobs
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Last 7 days
        </Typography>
        <Tooltip title="Refresh">
          <IconButton size="small" onClick={load} disabled={loading} aria-label="Refresh job status">
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Box>

      {loading && rows.length === 0 ? (
        <LinearProgress />
      ) : error ? (
        <Typography color="error" variant="body2" role="alert">
          {error}
        </Typography>
      ) : (
        <TableContainer>
          <Table size="small" aria-label="Background jobs status">
            <caption style={{ position: 'absolute', left: -10000 }}>
              Last status, duration, and next scheduled run for each background job.
            </caption>
            <TableHead>
              <TableRow>
                <TableCell scope="col" sx={{ width: 40 }} />
                <TableCell scope="col">Job</TableCell>
                <TableCell scope="col">Status</TableCell>
                <TableCell scope="col">Detail</TableCell>
                <TableCell scope="col">Last run</TableCell>
                <TableCell scope="col">Duration</TableCell>
                <TableCell scope="col">Next run</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((row) => {
                const status = statusOf(row);
                const lastStartedAt = row.lastRun ? new Date(row.lastRun.startedAt) : null;
                const nextAt = row.nextRunAt ? new Date(row.nextRunAt) : null;
                const showStallElapsed = status === 'stalled' && lastStartedAt;
                const isOpen = expanded.has(row.name);

                return (
                  <React.Fragment key={row.name}>
                  <TableRow hover sx={{ '& > *': { borderBottom: isOpen ? 'none' : undefined } }}>
                    <TableCell sx={{ width: 40 }}>
                      <IconButton
                        size="small"
                        aria-label={isOpen ? 'Hide history' : 'Show history'}
                        onClick={() => toggleExpanded(row.name)}
                      >
                        {isOpen ? <ArrowUpIcon fontSize="small" /> : <ArrowDownIcon fontSize="small" />}
                      </IconButton>
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        <Box component="span" sx={{ fontWeight: 600 }}>
                          {row.label}
                        </Box>
                        <Box component="span" sx={{ color: 'text.secondary', ml: 1 }}>
                          - {row.scheduleLabel}
                        </Box>
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusChip status={status} />
                    </TableCell>
                    <TableCell>
                      {status === 'failed' && row.lastRun?.errorMessage ? (
                        <Tooltip title={row.lastRun.errorMessage}>
                          <Typography
                            variant="body2"
                            color="error"
                            sx={{
                              maxWidth: 260,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                              cursor: 'help',
                            }}
                          >
                            {row.lastRun.errorMessage}
                          </Typography>
                        </Tooltip>
                      ) : status === 'skipped' &&
                        row.lastRun?.summary &&
                        typeof row.lastRun.summary === 'object' &&
                        (row.lastRun.summary as any).reason ? (
                        <Typography variant="body2" color="text.secondary">
                          {(row.lastRun.summary as any).reason}
                        </Typography>
                      ) : showStallElapsed ? (
                        <Typography variant="body2" color="error">
                          running for {formatDistanceToNow(lastStartedAt!)} — likely orphaned
                        </Typography>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {lastStartedAt ? (
                        <Tooltip title={format(lastStartedAt, 'PPpp')}>
                          <Typography variant="body2" sx={{ cursor: 'help' }}>
                            {formatDistanceToNow(lastStartedAt, { addSuffix: true })}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography variant="body2">
                        {status === 'running'
                          ? 'running…'
                          : formatDuration(row.lastRun?.durationMs ?? null)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {nextAt ? (
                        <Tooltip title={format(nextAt, 'PPpp')}>
                          <Typography
                            variant="body2"
                            sx={{ cursor: 'help', color: theme.palette.text.secondary }}
                          >
                            {formatDistanceToNow(nextAt, { addSuffix: true })}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">
                          —
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                  <TableRow aria-hidden={!isOpen}>
                    <TableCell colSpan={7} sx={{ p: 0, border: 0 }}>
                      <Collapse in={isOpen} timeout="auto" unmountOnExit>
                        {isOpen && (
                          <Box sx={{ m: 1 }}>
                            <JobHistoryPanel jobName={row.name} />
                          </Box>
                        )}
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                );
              })}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Paper>
  );
};
